import { initHangout } from '../index.js';
import { initializeHangout } from './initialization-new.js';
import { HangoutState } from '../state/HangoutState.js';
import { storage } from '../../utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import {
    hydrateGlobalHistoryFromConversation,
    shouldFetchConversationFromBackend,
    getGlobalHistoryMessages,
    resolvePreferredLocalMessages,
    getTranscriptForLayout
} from '../shared/history-resolution.js';
import { checkAndSendWelcomeBackMessage } from './resume-handlers.js';
import { getCurrentLayoutMode } from './layout-mode.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { getNsfwEnabled } from '../../utils/userPrefs.js';
import { scenarioIsNsfw } from '../../utils/nsfwTags.js';
import { timeProgressionService } from '../services/TimeProgressionService.js';
import {
    applyScenarioBackdropToConfig,
    shouldSeedScenarioBackdrop
} from '../shared/session-backdrop.js';

const RESUME_ENTRYPOINTS = new Set(['rankings_resume', 'hangout_page_refresh']);

const startupPrefetchRegistry = {
    key: null,
    prefetch: null
};

const normalizeId = (value) => (value === undefined || value === null || value === '' ? null : String(value));

const getOrCreateStartupPrefetch = ({ scenarioId, characterIds = [] }) => {
    const normalizedScenarioId = scenarioId ? String(scenarioId) : null;
    if (!normalizedScenarioId) return null;
    const normalizedCharacterIds = [...new Set(
        (characterIds || [])
            .map((characterId) => (characterId === undefined || characterId === null || characterId === '' ? null : String(characterId)))
            .filter(Boolean)
    )];
    const prefetchKey = `${normalizedScenarioId}::${normalizedCharacterIds.join(',')}`;
    if (startupPrefetchRegistry.key === prefetchKey && startupPrefetchRegistry.prefetch) {
        return startupPrefetchRegistry.prefetch;
    }

    const hangoutServicePromise = import('../services/HangoutService.js')
        .then(({ HangoutService }) => HangoutService.getInstance());
    const scenarioPromise = hangoutServicePromise
        .then((hangoutService) => hangoutService.getScenarioDetails(normalizedScenarioId));
    const characterPromisesById = {};
    normalizedCharacterIds.forEach((characterId) => {
        characterPromisesById[characterId] = hangoutServicePromise
            .then((hangoutService) => hangoutService.getCharacterDetails(characterId))
            .catch(() => null);
    });

    const prefetch = {
        key: prefetchKey,
        scenarioId: normalizedScenarioId,
        characterPromisesById,
        scenarioPromise,
        startedAt: Date.now()
    };
    startupPrefetchRegistry.key = prefetchKey;
    startupPrefetchRegistry.prefetch = prefetch;
    return prefetch;
};

const shouldResetConversationStorage = ({ entrypoint, isPageRefresh, hasConversationId, hasPersistedLocalHistory }) => {
    if (entrypoint === 'hangout_page_refresh' && isPageRefresh && (hasConversationId || hasPersistedLocalHistory)) {
        return false;
    }
    return true;
};

export const ensureCharacterNames = async (config) => {
    try {
        if (!config || !Array.isArray(config.allCharacters) || config.allCharacters.length === 0) {
            return config;
        }

        const { isPlaceholderCharacterName } = await import('../../rankings/components/character-name-utils.js');
        const { HangoutService } = await import('../services/HangoutService.js');
        const hangoutService = HangoutService.getInstance();
        let changed = false;

        for (const character of config.allCharacters) {
            if (!character.id || !isPlaceholderCharacterName(character.name, character.id)) continue;
            try {
                const details = await hangoutService.getCharacterDetails(String(character.id));
                if (details?.name && details.name !== character.name) {
                    character.name = details.name;
                    changed = true;
                }
            } catch { /* non-fatal */ }
        }

        if (changed) {
            await storageManager.setItem('currentHangoutConfig', config);
        }
    } catch (error) {
        console.warn('[Hangout] Failed to enrich character names:', error);
    }
    return config;
};

const seedScenarioBackdrop = async (config, startupPrefetch) => {
    storage.remove('hangout_user_time_override');
    storage.remove('hangout_manual_time_set');

    let scenario = null;
    try {
        scenario = startupPrefetch?.scenarioPromise ? await startupPrefetch.scenarioPromise : null;
    } catch (error) {
        console.warn('[Hangout] Failed to load scenario backdrop:', error);
    }

    const next = applyScenarioBackdropToConfig(config, scenario);
    if (scenario) next.selectedScenario = scenario;
    await storageManager.setItem('currentHangoutConfig', next);
    timeProgressionService.primeFromStoredConfig();
    return next;
};

const clearPreviousConversationState = async (config, conversationId) => {
    const hangoutState = HangoutState.getInstance();
    await hangoutState.setConversationId(null);
    hangoutState.setMessages([]);
    if (hangoutState.state) {
        hangoutState.state.messageHistory = [];
        hangoutState.state.currentSession = null;
    }

    if (conversationId) {
        storage.removeConversation(conversationId);
    }

    try {
        const { cleanupRemovedCharacters } = await import('./character-manager.js');
        await cleanupRemovedCharacters();
    } catch (cleanupError) {
        console.warn('[Hangout] Error cleaning up character containers:', cleanupError);
    }

    if (typeof window !== 'undefined' && window.clearConversationCache) {
        window.clearConversationCache();
    }

    try {
        const chatContainer = document.querySelector('.chat-container');
        if (chatContainer) chatContainer.innerHTML = '';
    } catch (uiError) {
        console.warn('[Hangout] Error clearing UI elements:', uiError);
    }

    await storageManager.setItem('currentHangoutConfig', {
        ...config,
        isNewConversation: true,
        conversationId: null
    });
    hangoutState.setGreetingVariationMeta({ variation: 0, count: 1 });
    hangoutState.clearGreetingVariationCache();
};

export const startHangoutSession = async (config, { element, isPageRefresh = false } = {}) => {
    if (!element) {
        throw new Error('Hangout container not found');
    }

    let sessionConfig = {
        ...(config || {}),
        preserveLocalBackdrop: isPageRefresh === true
    };
    await storageManager.setItem('currentHangoutConfig', sessionConfig);
    if (isPageRefresh) {
        timeProgressionService.primeFromStoredConfig();
    }

    const hangoutState = HangoutState.getInstance();
    const hasConversationInConfig = !!normalizeId(sessionConfig?.conversationId);
    const hasPersistedLocalHistory = getGlobalHistoryMessages().length > 0;
    const requiresConversationReset = shouldResetConversationStorage({
        entrypoint: sessionConfig?.entrypoint,
        isPageRefresh,
        hasConversationId: hasConversationInConfig,
        hasPersistedLocalHistory
    });

    if (requiresConversationReset) {
        try {
            await hangoutState.clearConversationStorageKeys();
        } catch (error) {
            console.warn('[Hangout] Failed clearing conversation storage keys:', error);
        }
    }

    const normalizedConversationId = normalizeId(sessionConfig?.conversationId);
    if (requiresConversationReset && sessionConfig?.isNewConversation === true && normalizedConversationId) {
        sessionConfig = { ...sessionConfig, conversationId: null };
        await storageManager.setItem('currentHangoutConfig', sessionConfig);
    }

    const hydrationConversationId = normalizeId(sessionConfig?.conversationId);
    const shouldHydrateFromBackend = !!hydrationConversationId
        && sessionConfig?.isNewConversation !== true
        && (!requiresConversationReset || RESUME_ENTRYPOINTS.has(sessionConfig?.entrypoint));

    if (shouldHydrateFromBackend) {
        try {
            const { hydrated, messages } = hydrateGlobalHistoryFromConversation(hydrationConversationId);
            if (!hydrated && messages.length === 0 && shouldFetchConversationFromBackend(hydrationConversationId)) {
                await storage.getCompleteConversation(hydrationConversationId);
            }
            sessionConfig = await ensureCharacterNames(await storageManager.getItem('currentHangoutConfig'));
        } catch (error) {
            console.warn('[Hangout] Pre-seed from backend failed:', error);
        }
    }

    let characterId = normalizeId(sessionConfig?.allCharacters?.[0]?.id);
    let scenarioId = normalizeId(sessionConfig?.scenarioId);
    const outfitId = normalizeId(sessionConfig?.allCharacters?.[0]?.outfitId) || '0';
    let conversationId = sessionConfig?.conversationId;
    let isResuming = !sessionConfig?.isNewConversation && !!conversationId;

    if (!characterId || !scenarioId) {
        const refreshed = await storageManager.getItem('currentHangoutConfig');
        if (refreshed) {
            if (!characterId) characterId = normalizeId(refreshed.allCharacters?.[0]?.id || refreshed.characterId);
            if (!scenarioId) scenarioId = normalizeId(refreshed.scenarioId);
            sessionConfig = refreshed;
        }
        if (!characterId || !scenarioId) {
            throw new Error('Missing character or scenario ID in configuration');
        }
    }

    const startupCharacterIds = Array.isArray(sessionConfig?.allCharacters)
        ? sessionConfig.allCharacters.map((character) => normalizeId(character?.id)).filter(Boolean)
        : [];
    if (characterId && !startupCharacterIds.includes(characterId)) {
        startupCharacterIds.push(characterId);
    }
    const startupPrefetch = getOrCreateStartupPrefetch({
        scenarioId,
        characterIds: startupCharacterIds
    });
    if (startupPrefetch) {
        hangoutState.setStartupPrefetch(startupPrefetch);
    }

    if (!isResuming && conversationId) {
        const { messages: existingMessages } = resolvePreferredLocalMessages(conversationId);
        if (existingMessages.length > 0) {
            isResuming = true;
            if (sessionConfig?.isNewConversation) {
                try {
                    sessionConfig = { ...sessionConfig, isNewConversation: false };
                    await storageManager.setItem('currentHangoutConfig', sessionConfig);
                } catch (error) {
                    console.warn('[Hangout] Failed to persist isNewConversation=false for refresh resume:', error);
                }
            }
        }
    }

    if (conversationId && sessionConfig?.isNewConversation && !isResuming) {
        isResuming = false;
    }

    if (!isResuming && shouldSeedScenarioBackdrop(sessionConfig)) {
        sessionConfig = await seedScenarioBackdrop(sessionConfig, startupPrefetch);
    }

    const branch = isResuming && conversationId && !sessionConfig?.isNewConversation ? 'resume' : 'new';
    console.info('[Hangout][boot]', {
        entrypoint: sessionConfig?.entrypoint,
        isNewConversation: sessionConfig?.isNewConversation === true,
        conversationId: conversationId || null,
        historyCount: getGlobalHistoryMessages().length,
        visibleCount: getTranscriptForLayout(hangoutState).length,
        layout: getCurrentLayoutMode(),
        branch
    });

    if (branch === 'resume') {
        await initializeHangout();
        await checkAndSendWelcomeBackMessage();
        return;
    }

    if (!getNsfwEnabled() && scenarioId) {
        const selectedScenario = sessionConfig?.selectedScenario;
        const knowsNsfw = selectedScenario && (
            selectedScenario.isN != null
            || selectedScenario.is_nsfw != null
            || Array.isArray(selectedScenario.tags)
        );
        if (scenarioIsNsfw(selectedScenario)) {
            window.location.href = './rankings.html';
            return;
        }
        if (!knowsNsfw && !sessionConfig?.isPrivateScenario) {
            try {
                const scenarios = await fetchCache.fetchScenarios('en');
                const list = Array.isArray(scenarios) ? scenarios : (scenarios?.scenarios || []);
                const found = list.find((s) => String(s.id) === String(scenarioId));
                if (!found || scenarioIsNsfw(found)) {
                    window.location.href = './rankings.html';
                    return;
                }
            } catch (error) {
                console.warn('[Hangout] Failed to verify scenario for Filtered mode:', error);
            }
        } else if (!knowsNsfw) {
            try {
                const details = sessionConfig?.selectedScenario
                    || (startupPrefetch?.scenarioPromise ? await startupPrefetch.scenarioPromise : null);
                if (scenarioIsNsfw(details)) {
                    window.location.href = './rankings.html';
                    return;
                }
            } catch (error) {
                console.warn('[Hangout] Failed to verify private scenario for Filtered mode:', error);
            }
        }
    }

    if (requiresConversationReset) {
        try {
            await clearPreviousConversationState(sessionConfig, conversationId);
        } catch (error) {
            console.error('[Hangout] Error clearing previous conversation state:', error);
        }
    }

    await initHangout({
        element,
        characterId,
        scenarioId,
        outfitId,
        startupPrefetch
    });
    await initializeHangout();
    await checkAndSendWelcomeBackMessage();
};
